import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { DataFormContent } from './DataFormContent'
import { useGetDataForm } from './useGetDataForm'
import { TileFilter } from '../shared/TileFilter'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { useCallback, useMemo, useState } from 'react'
import { FormContentV2 } from '../FormComponent/FormContentV2'
import { InferFormValues, JsonFormSchemaV2 } from '../FormComponent/types'
import { createDefaultValues } from '../FormComponent/utils'
import { synchronizeDataFormAfterSubmit } from './utils'

export const DataFormComponent = <TSchema extends JsonFormSchemaV2>(props: {
  id: string
  resetDefaults?: InferFormValues<TSchema>
  className?: string
}) => {
  const { tileFilterSelections } = useTileFilters()
  const { id, resetDefaults: providedResetDefaults, ...rest } = props
  const { data, refetch } = useGetDataForm(id, tileFilterSelections)
  const [resetVersion, setResetVersion] = useState(0)
  const {
    dataDisplay: { type: dataDisplayType },
  } = data.uiSchema

  const dataFormResetDefaults = useMemo(() => {
    const defaults = createDefaultValues(data.uiSchema.schema.properties)
    if (resetVersion === 0 && providedResetDefaults) {
      return { ...defaults, ...providedResetDefaults }
    }
    return resetVersion === 0 ? defaults : { ...defaults }
  }, [data.uiSchema.schema.properties, providedResetDefaults, resetVersion])

  type FormData = InferFormValues<typeof data.uiSchema.schema>
  const {
    actions: { handlerId },
  } = data.uiSchema

  const { mutateAsync: submitForm, isPending: isSubmitting } = useSubmitForm(handlerId, tileFilterSelections)

  const refreshDataForm = useCallback(async () => {
    const result = await refetch()
    if (result.isSuccess) {
      setResetVersion((version) => version + 1)
    }
    return result
  }, [refetch])

  const submitHandler = (formData: FormData) => {
    void synchronizeDataFormAfterSubmit(
      () => submitForm(formData),
      refreshDataForm
    ).catch(() => undefined)
  }

  if (dataDisplayType === 'none') {
    return (
      <FormContentV2
        schema={data.uiSchema.schema}
        submitHandler={submitHandler}
        tileFilters={<TileFilter schema={data.filters} />}
        isSubmitting={isSubmitting}
        {...rest}
        resetDefaults={dataFormResetDefaults}
      />
    )
  }

  return (
    <DataFormContent
      tileFilters={<TileFilter schema={data.filters} />}
      uiSchema={data.uiSchema}
      formValues={data.dataSchema.formData}
    />
  )
}

export function DataFormComponentLoading() {
  return 'Loading...'
}
